import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Modal } from '@/features/admin/Modal';
import { billingApi, GATEWAY_PROVIDERS, type GatewayProvider } from '@/api/endpoints/billing';

const WEBHOOK_ASAAS = 'https://nautmax.trackmax.com.br/geo/v1/billing/webhook/asaas';
const WEBHOOK_MP = 'https://nautmax.trackmax.com.br/geo/v1/billing/webhook/mercadopago';

/** Editor de gateway que guarda AS DUAS estruturas (Asaas + Mercado Pago) e o provider ativo.
 * Usado pelo financeiro/revenda (próprio tenant) e pelo ADMIN (por revenda). */
export function GatewayModal({
  tenantId,
  label,
  onClose,
}: {
  tenantId: number;
  label?: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const gwQ = useQuery({ queryKey: ['billing', 'gateway', tenantId], queryFn: () => billingApi.getGateway(tenantId) });
  const gw = gwQ.data;

  const [form, setForm] = useState<{
    provider: GatewayProvider;
    asaas_api_key: string;
    asaas_webhook_token: string;
    mp_access_token: string;
    mp_webhook_token: string;
    sandbox: boolean;
    enabled: boolean;
  } | null>(null);

  const cur =
    form ??
    (gw
      ? {
          provider: gw.provider,
          asaas_api_key: '',
          asaas_webhook_token: '',
          mp_access_token: '',
          mp_webhook_token: '',
          sandbox: gw.sandbox,
          enabled: gw.enabled,
        }
      : {
          provider: 'asaas' as GatewayProvider,
          asaas_api_key: '',
          asaas_webhook_token: '',
          mp_access_token: '',
          mp_webhook_token: '',
          sandbox: true,
          enabled: false,
        });

  const save = useMutation({
    mutationFn: () =>
      billingApi.setGateway(tenantId, {
        provider: cur.provider,
        // só envia o que foi digitado (senão mantém o atual)
        asaas_api_key: cur.asaas_api_key ? cur.asaas_api_key : null,
        asaas_webhook_token: cur.asaas_webhook_token ? cur.asaas_webhook_token : null,
        mp_access_token: cur.mp_access_token ? cur.mp_access_token : null,
        mp_webhook_token: cur.mp_webhook_token ? cur.mp_webhook_token : null,
        sandbox: cur.sandbox,
        enabled: cur.enabled,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['billing', 'gateway', tenantId] });
      onClose();
    },
  });

  return (
    <Modal
      title={`Gateway de Cobrança${label ? ` — ${label}` : ''}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || gwQ.isLoading}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      {gwQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : (
        <div className="space-y-4">
          <p className="text-[11px] text-muted-foreground">
            Preencha as credenciais dos dois provedores e escolha qual fica <strong>ativo</strong> para gerar as cobranças.
          </p>

          {/* Asaas */}
          <fieldset className="space-y-2 rounded-md border border-border p-3">
            <legend className="px-1 text-xs font-bold">Asaas</legend>
            <L label={`Chave de API ${gw?.asaas_api_key_mask ? `(atual: ${gw.asaas_api_key_mask})` : ''}`}>
              <Input
                type="password"
                value={cur.asaas_api_key}
                onChange={(e) => setForm({ ...cur, asaas_api_key: e.target.value })}
                placeholder={gw?.asaas_api_key_mask ? 'deixe em branco para manter' : 'access_token do Asaas'}
              />
            </L>
            <L label={`Token do webhook ${gw?.asaas_webhook_set ? '(definido)' : '(opcional)'}`}>
              <Input
                type="password"
                value={cur.asaas_webhook_token}
                onChange={(e) => setForm({ ...cur, asaas_webhook_token: e.target.value })}
                placeholder={gw?.asaas_webhook_set ? 'deixe em branco para manter' : 'opcional'}
              />
            </L>
            <p className="text-[10px] text-muted-foreground">Webhook no painel Asaas: <code className="break-all">{WEBHOOK_ASAAS}</code></p>
          </fieldset>

          {/* Mercado Pago */}
          <fieldset className="space-y-2 rounded-md border border-border p-3">
            <legend className="px-1 text-xs font-bold">Mercado Pago</legend>
            <L label={`Access Token ${gw?.mp_access_token_mask ? `(atual: ${gw.mp_access_token_mask})` : ''}`}>
              <Input
                type="password"
                value={cur.mp_access_token}
                onChange={(e) => setForm({ ...cur, mp_access_token: e.target.value })}
                placeholder={gw?.mp_access_token_mask ? 'deixe em branco para manter' : 'APP_USR-... / TEST-...'}
              />
            </L>
            <L label={`Token do webhook ${gw?.mp_webhook_set ? '(definido)' : '(opcional)'}`}>
              <Input
                type="password"
                value={cur.mp_webhook_token}
                onChange={(e) => setForm({ ...cur, mp_webhook_token: e.target.value })}
                placeholder={gw?.mp_webhook_set ? 'deixe em branco para manter' : 'opcional'}
              />
            </L>
            <p className="text-[10px] text-muted-foreground">Webhook no painel Mercado Pago: <code className="break-all">{WEBHOOK_MP}</code></p>
          </fieldset>

          {/* Ativo + sandbox + enabled */}
          <L label="Provedor ativo (usado para gerar cobranças)">
            <select
              value={cur.provider}
              onChange={(e) => setForm({ ...cur, provider: e.target.value as GatewayProvider })}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {GATEWAY_PROVIDERS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </L>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={cur.sandbox} onChange={(e) => setForm({ ...cur, sandbox: e.target.checked })} />
            Ambiente sandbox (teste)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={cur.enabled} onChange={(e) => setForm({ ...cur, enabled: e.target.checked })} />
            Gateway ativo (emite boleto/PIX)
          </label>
          {save.error && <p className="text-xs text-status-blocked">{(save.error as Error).message}</p>}
        </div>
      )}
    </Modal>
  );
}

function L({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
